import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ProductList from '../components/products/ProductList';
import ProductDetails from '../components/products/ProductDetails';
import ProductForm from '../components/products/ProductForm';
import { db } from '../lib/supabase';
import type { Product, StockMovement } from '../types/product';

const Products: React.FC = () => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const location = useLocation();

  useEffect(() => {
    const state = location.state as { openNewProduct?: boolean };
    if (state?.openNewProduct) {
      setIsFormOpen(true);
      // Clear location state to prevent reopening form on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const data = await db.products.list();
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handleProductSubmit = async (data: Product) => {
    try {
      if (editingProduct) {
        await db.products.update(editingProduct.id, data);
      } else {
        await db.products.create(data);
      }
      await loadProducts();
      setIsFormOpen(false);
      setEditingProduct(null);
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleSelectProduct = (product: Product | null) => {
    if (product === null) {
      // This means we want to create a new product
      setIsFormOpen(true);
      setEditingProduct(null);
      setSelectedProduct(null);
    } else {
      setSelectedProduct(product);
      setIsFormOpen(false);
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setIsFormOpen(true);
  };

  const handleStockMovement = (type: 'in' | 'out', quantity: number) => {
    // Implement stock movement functionality
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Gestão de Produtos e Estoque
          </h1>
          <button
            onClick={() => handleSelectProduct(null)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Novo Produto
          </button>
        </div>

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow p-4">
            <h2 className="text-lg font-semibold mb-4">Filtros</h2>
          </div>

          <div className="col-span-5">
            <ProductList
              products={products}
              onSelect={handleSelectProduct}
            />
          </div>

          <div className="col-span-5">
            {isFormOpen ? (
              <ProductForm
                initialData={editingProduct || undefined}
                onSubmit={handleProductSubmit}
                onCancel={() => {
                  setIsFormOpen(false);
                  setEditingProduct(null);
                }}
              />
            ) : selectedProduct ? (
              <ProductDetails
                product={selectedProduct}
                stockMovements={[]}
                onEdit={() => handleEdit(selectedProduct)}
                onStockMovement={handleStockMovement}
              />
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                Selecione um produto para ver os detalhes
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;